import { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, DollarSign, Hash, Building2, Mail, Clock, Check, X, Edit2, Save, RefreshCw, Search, ChevronDown, ChevronUp, Eye, Trash2, Download, Plus, Package, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import FulfillmentTracker from './FulfillmentTracker';
import WinRateStrip from '@/components/WinRateStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');

const STATUS_COLORS = {
  draft:    'bg-gray-100 text-gray-600',
  sent:     'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  expired:  'bg-amber-100 text-amber-700',
  closed:   'bg-purple-100 text-purple-700',
  revised:  'bg-orange-100 text-orange-700',
  won:      'bg-indigo-100 text-indigo-700',
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (val) => `$${Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_LABELS = {
  single_grade: 'Single Grade Packs',
  bundle: 'Print Bundles',
  digital: 'Digital Licenses',
  complete: 'Complete Bundles',
  teacher_tools: 'Teacher Guide & Tool Kit',
  toolkit: 'Teacher Toolkit (Digital Add-On)',
  sses: 'Student Stability & Engagement — Student Kits (K-8)',
  custom: 'Custom Products',
};

const QuotesManagementTab = () => {
  const [activeView, setActiveView] = useState('quotes'); // 'quotes' | 'pricing'
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'closed' | 'accepted_revenue'
  const [editingPrices, setEditingPrices] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [revisingQuote, setRevisingQuote] = useState(null);
  const [revisionItems, setRevisionItems] = useState([]);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [revisionSendEmail, setRevisionSendEmail] = useState(true);
  const [revisionEffectiveDate, setRevisionEffectiveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ sku: '', name: '', category: 'custom', grade: '', unit: 'per unit', price: '', min_qty: 1, grades: 1, type: 'custom' });
  const [addingProduct, setAddingProduct] = useState(false);

  // ── Bulk selection + bulk-delete modal state ──
  const [selected, setSelected] = useState(() => new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeletePassword, setBulkDeletePassword] = useState('');
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, pRes] = await Promise.all([
        axios.get(`${API}/quotes/list?limit=200`, { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API}/quotes/products`),
      ]);
      setQuotes(qRes.data.quotes || []);
      setProducts(pRes.data.products || []);
    } catch (err) {
      console.error('Failed to fetch quotes data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (quoteNum, newStatus) => {
    try {
      await axios.put(`${API}/quotes/${quoteNum}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const now = new Date().toISOString();
      setQuotes(prev => prev.map(q => q.quote_number === quoteNum ? { ...q, status: newStatus, status_updated_at: now } : q));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const startRevision = (quote) => {
    setRevisingQuote(quote);
    setRevisionItems(quote.items.map(i => ({ sku: i.sku, quantity: i.quantity })));
    setRevisionNotes(quote.notes || '');
    setRevisionSendEmail(true);
    setRevisionEffectiveDate(new Date().toISOString().slice(0, 10));
  };

  const submitRevision = async () => {
    if (!revisingQuote) return;
    setSubmittingRevision(true);
    try {
      const res = await axios.post(
        `${API}/quotes/${revisingQuote.quote_number}/revise`,
        { items: revisionItems, notes: revisionNotes, send_email: revisionSendEmail, effective_date: revisionEffectiveDate },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Revision ${res.data.quote_number} created`, {
        description: `Subtotal: ${formatCurrency(res.data.subtotal)}${res.data.email_sent ? ' · email sent' : ''}`,
      });
      setRevisingQuote(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to create revision', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSubmittingRevision(false);
    }
  };


  const deleteQuote = async (quoteNum) => {
    if (!window.confirm(`Delete quote ${quoteNum}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/quotes/${quoteNum}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setQuotes(prev => prev.filter(q => q.quote_number !== quoteNum));
      if (expandedQuote === quoteNum) setExpandedQuote(null);
    } catch (err) {
      console.error('Failed to delete quote:', err);
    }
  };

  const handleFulfillmentUpdate = (quoteNum, fulfillment) => {
    setQuotes(prev => prev.map(q => q.quote_number === quoteNum ? { ...q, fulfillment } : q));
  };

  const downloadQuotePdf = (quoteNum) => {
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/blm/file/MOSAIC_Quote_${quoteNum}.pdf`;
    window.open(pdfUrl, '_blank');
  };

  const savePricing = async () => {
    const updates = Object.entries(editingPrices).map(([sku, price]) => ({
      sku,
      price: parseFloat(price) || 0,
    }));
    if (updates.length === 0) return;
    setSavingPrices(true);
    try {
      await axios.put(`${API}/quotes/products/pricing`, updates, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      setEditingPrices({});
      fetchData();
    } catch (err) {
      console.error('Failed to save pricing:', err);
    } finally {
      setSavingPrices(false);
    }
  };

  const addProduct = async () => {
    if (!newProduct.sku.trim() || !newProduct.name.trim()) {
      toast.error('SKU and Name are required');
      return;
    }
    setAddingProduct(true);
    try {
      await axios.post(`${API}/quotes/products`, {
        ...newProduct,
        price: parseFloat(newProduct.price) || 0,
        min_qty: parseInt(newProduct.min_qty) || 1,
        grades: parseInt(newProduct.grades) || 1,
      }, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      setNewProduct({ sku: '', name: '', category: 'custom', grade: '', unit: 'per unit', price: '', min_qty: 1, grades: 1, type: 'custom' });
      setShowAddProduct(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to add product', {
        description: err.response?.data?.detail,
      });
    } finally {
      setAddingProduct(false);
    }
  };

  const deleteProduct = async (sku) => {
    if (!window.confirm(`Delete custom product ${sku}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/quotes/products/${sku}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to delete product', {
        description: err.response?.data?.detail,
      });
    }
  };

  // Stats
  const totalQuotes = quotes.length;
  const totalRevenue = quotes.filter(q => q.status === 'accepted' || q.status === 'closed').reduce((s, q) => s + (q.subtotal || 0), 0);
  const pendingQuotes = quotes.filter(q => q.status === 'draft' || q.status === 'sent').length;
  const closedQuotes = quotes.filter(q => q.status === 'closed').length;
  const sumOf = (list) => list.reduce((s, q) => s + (q.subtotal || 0), 0);
  const totalAmount = sumOf(quotes);
  const pendingAmount = sumOf(quotes.filter(q => q.status === 'draft' || q.status === 'sent'));
  const closedAmount = sumOf(quotes.filter(q => q.status === 'closed'));

  const filtered = quotes.filter(q => {
    // KPI-card status filter
    if (statusFilter === 'pending' && !(q.status === 'draft' || q.status === 'sent')) return false;
    if (statusFilter === 'closed' && q.status !== 'closed') return false;
    if (statusFilter === 'accepted_revenue' && !(q.status === 'accepted' || q.status === 'closed')) return false;
    // Text search
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (q.quote_number || '').toLowerCase().includes(t) ||
           (q.contact?.school_name || '').toLowerCase().includes(t) ||
           (q.contact?.contact_name || '').toLowerCase().includes(t) ||
           (q.contact?.school_dbn || '').toLowerCase().includes(t);
  });

  // ── Detect TEST / DEMO / SPAM quotes for fast clean-up ──
  // Matches common throwaway patterns in school_name, contact_name, contact_email, notes.
  const SPAM_REGEX = /\b(test|demo|sample|spam|qa|dummy|dev|fake|asdf|xxx|aaaa|bbbb|foo|bar)\b/i;
  const isSuspectQuote = useCallback((q) => {
    const hay = [
      q.contact?.school_name, q.contact?.contact_name,
      q.contact?.contact_email, q.contact?.school_dbn,
      q.notes, q.quote_number,
    ].filter(Boolean).join(' | ');
    return SPAM_REGEX.test(hay);
  }, []);
  const suspectCount = useMemo(() => filtered.filter(isSuspectQuote).length, [filtered, isSuspectQuote]);

  // ── Selection helpers ──
  const toggleOne = (qnum) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(qnum)) next.delete(qnum); else next.add(qnum);
      return next;
    });
  };
  const toggleAllVisible = () => {
    const visibleKeys = filtered.map(q => q.quote_number);
    const allSelected = visibleKeys.every(k => selected.has(k)) && visibleKeys.length > 0;
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach(k => next.delete(k));
      else visibleKeys.forEach(k => next.add(k));
      return next;
    });
  };
  const selectSuspects = () => {
    const suspects = filtered.filter(isSuspectQuote).map(q => q.quote_number);
    setSelected(prev => {
      const next = new Set(prev);
      suspects.forEach(k => next.add(k));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // ── Bulk delete submit ──
  const submitBulkDelete = async () => {
    if (selected.size === 0) return;
    if (bulkDeleteConfirmText.trim().toUpperCase() !== 'DELETE') return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(
        `${API}/quotes/bulk-delete`,
        {
          quote_numbers: Array.from(selected),
          password: bulkDeletePassword,
          reason: bulkDeleteReason,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const { deleted_count = 0, skipped = [] } = res.data || {};
      const skippedLinked = skipped.filter(s => s.reason === 'linked_to_order');
      const skippedMissing = skipped.filter(s => s.reason === 'not_found');
      const desc_lines = [];
      if (skippedLinked.length) {
        desc_lines.push(`${skippedLinked.length} skipped — linked to an Order (delete the Order first): ` +
                        skippedLinked.map(s => `${s.quote_number} → ${s.order_number}`).join(', '));
      }
      if (skippedMissing.length) {
        desc_lines.push(`${skippedMissing.length} skipped — not found.`);
      }
      toast.success(`Deleted ${deleted_count} quote${deleted_count === 1 ? '' : 's'}`, {
        description: desc_lines.length ? desc_lines.join(' · ') : undefined,
      });
      clearSelection();
      setShowBulkDelete(false);
      setBulkDeletePassword('');
      setBulkDeleteReason('');
      setBulkDeleteConfirmText('');
      fetchData();
    } catch (err) {
      toast.error('Bulk delete failed', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const grouped = {};
  products.forEach(p => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  const hasEdits = Object.keys(editingPrices).length > 0;

  return (
    <>
    <div className="space-y-5" data-testid="quotes-management-tab">
      {/* Win Rate analytics strip — only shown on the Quotes view */}
      {activeView === 'quotes' && <WinRateStrip />}

      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={activeView === 'quotes' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('quotes')}
          className={activeView === 'quotes' ? 'bg-[#002FA7]' : ''}
          data-testid="view-quotes-btn"
        >
          <FileText size={14} className="mr-1" /> Quotes ({totalQuotes})
        </Button>
        <Button
          variant={activeView === 'pricing' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('pricing')}
          className={activeView === 'pricing' ? 'bg-[#002FA7]' : ''}
          data-testid="view-pricing-btn"
        >
          <DollarSign size={14} className="mr-1" /> Product Pricing
        </Button>
      </div>

      {activeView === 'quotes' && (
        <>
          {/* Stats — clickable filter cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')}
              className={`bg-white border-2 p-4 text-left transition-all hover:shadow-md hover:border-[#002FA7] ${statusFilter === 'all' ? 'border-[#002FA7] ring-2 ring-[#002FA7]/20' : 'border-gray-100'}`}
              data-testid="stat-total-quotes"
              title="Show all quotes"
            >
              <FileText size={18} className="text-[#002FA7]" />
              <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{totalQuotes}</div>
              <div className="text-xs text-gray-500 font-medium">Total Quotes</div>
              <div className="text-sm font-bold text-[#002FA7] mt-0.5" data-testid="stat-total-quotes-amount">{formatCurrency(totalAmount)}</div>
              {statusFilter === 'all' && <div className="text-[10px] text-[#002FA7] font-bold mt-0.5">▸ showing all</div>}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
              className={`bg-white border-2 p-4 text-left transition-all hover:shadow-md hover:border-amber-500 ${statusFilter === 'pending' ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/30' : 'border-gray-100'}`}
              data-testid="stat-pending-quotes"
              title="Click to filter to Draft + Sent quotes"
            >
              <Clock size={18} className="text-amber-500" />
              <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{pendingQuotes}</div>
              <div className="text-xs text-gray-500 font-medium">Pending</div>
              <div className="text-sm font-bold text-amber-700 mt-0.5" data-testid="stat-pending-quotes-amount">{formatCurrency(pendingAmount)}</div>
              {statusFilter === 'pending' && <div className="text-[10px] text-amber-700 font-bold mt-0.5">▸ filter active</div>}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'closed' ? 'all' : 'closed')}
              className={`bg-white border-2 p-4 text-left transition-all hover:shadow-md hover:border-emerald-500 ${statusFilter === 'closed' ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/30' : 'border-gray-100'}`}
              data-testid="stat-accepted-quotes"
              title="Click to filter to Closed (Order In) quotes"
            >
              <Check size={18} className="text-emerald-500" />
              <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{closedQuotes}</div>
              <div className="text-xs text-gray-500 font-medium">Closed (Order In)</div>
              <div className="text-sm font-bold text-emerald-700 mt-0.5" data-testid="stat-closed-quotes-amount">{formatCurrency(closedAmount)}</div>
              {statusFilter === 'closed' && <div className="text-[10px] text-emerald-700 font-bold mt-0.5">▸ filter active</div>}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter(statusFilter === 'accepted_revenue' ? 'all' : 'accepted_revenue')}
              className={`bg-white border-2 p-4 text-left transition-all hover:shadow-md hover:border-[#002FA7] ${statusFilter === 'accepted_revenue' ? 'border-[#002FA7] ring-2 ring-[#002FA7]/20 bg-blue-50/30' : 'border-gray-100'}`}
              data-testid="stat-total-revenue"
              title="Click to filter to revenue-generating quotes (Accepted + Closed)"
            >
              <DollarSign size={18} className="text-[#002FA7]" />
              <div className="text-2xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(totalRevenue)}</div>
              <div className="text-xs text-gray-500 font-medium">Accepted Revenue</div>
              {statusFilter === 'accepted_revenue' && <div className="text-[10px] text-[#002FA7] font-bold mt-0.5">▸ filter active</div>}
            </button>
          </div>

          {/* Active filter bar */}
          {statusFilter !== 'all' && (
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs" data-testid="active-filter-bar">
              <span className="font-semibold text-slate-700">Active filter:</span>
              <span className="bg-white border border-slate-300 rounded px-2 py-0.5 font-medium text-slate-800">
                {statusFilter === 'pending' && 'Pending (Draft + Sent)'}
                {statusFilter === 'closed' && 'Closed (Order In)'}
                {statusFilter === 'accepted_revenue' && 'Revenue-generating (Accepted + Closed)'}
              </span>
              <span className="text-slate-500 ml-1">· {filtered.length} of {quotes.length} quotes shown</span>
              <button
                onClick={() => setStatusFilter('all')}
                className="ml-auto text-[#002FA7] hover:underline font-bold"
                data-testid="clear-status-filter-btn"
              >
                Clear filter
              </button>
            </div>
          )}

          {/* Search + bulk-selection toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by quote #, school, DBN, or contact..." className="pl-9 text-sm" data-testid="quotes-search" />
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-quotes-btn">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </Button>
            {suspectCount > 0 && (
              <Button
                variant="outline" size="sm"
                onClick={selectSuspects}
                className="text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
                title="Select quotes whose name/email/notes contain TEST, DEMO, SAMPLE, SPAM, QA, DUMMY, DEV, FAKE, etc."
                data-testid="select-suspects-btn"
              >
                <AlertTriangle size={14} className="mr-1" /> Select {suspectCount} TEST/DEMO/SPAM
              </Button>
            )}
            {selected.size > 0 && (
              <>
                <span className="text-sm text-gray-600" data-testid="selection-count">
                  <b>{selected.size}</b> selected
                </span>
                <Button size="sm" variant="outline" onClick={clearSelection} data-testid="clear-selection-btn">
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowBulkDelete(true)}
                  className="bg-red-600 hover:bg-red-700 text-white gap-1"
                  data-testid="open-bulk-delete-btn"
                >
                  <Trash2 size={14} /> Delete {selected.size}
                </Button>
              </>
            )}
          </div>

          {/* Quotes Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="quotes-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="w-8 px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      aria-label="Select all visible quotes"
                      checked={filtered.length > 0 && filtered.every(q => selected.has(q.quote_number))}
                      onChange={toggleAllVisible}
                      data-testid="select-all-visible"
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Quote #</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Date</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">School</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">DBN</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Contact</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Amount</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Status</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Order #</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Last Action</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">No quotes yet. Share <span className="font-mono text-[#002FA7]">/quote</span> with schools to start receiving quote requests.</td></tr>
                ) : filtered.map((q, i) => (
                  <tr key={q.quote_number} className={`border-b hover:bg-blue-50/30 transition-colors group ${selected.has(q.quote_number) ? 'bg-blue-50/60' : ''} ${isSuspectQuote(q) ? 'bg-amber-50/40' : ''}`} data-testid={`quote-row-${i}`}>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`Select ${q.quote_number}`}
                        checked={selected.has(q.quote_number)}
                        onChange={() => toggleOne(q.quote_number)}
                        data-testid={`select-quote-${i}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono font-bold text-[#002FA7] text-[10px]">
                      <button onClick={() => downloadQuotePdf(q.quote_number)} className="hover:underline flex items-center gap-1" title="Click to view/download PDF" data-testid={`quote-link-${i}`}>
                        <Download size={10} className="opacity-0 group-hover:opacity-100" />
                        {q.quote_number}
                        {q.revision_number > 0 && (
                          <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-700 font-bold border border-orange-300">REV{q.revision_number}</span>
                        )}
                        {q.revised_to && (
                          <span className="ml-1 text-[9px] text-gray-400">→ {q.revised_to}</span>
                        )}
                        {isSuspectQuote(q) && (
                          <span className="ml-1 text-[9px] px-1 py-0.5 bg-amber-200 text-amber-900 font-bold border border-amber-400 uppercase tracking-wider">Test</span>
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(q.created_at)}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 max-w-[140px] truncate">{q.contact?.school_name}</td>
                    <td className="px-3 py-2 font-mono text-gray-500">{q.contact?.school_dbn}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{q.contact?.contact_name}</td>
                    <td className="px-3 py-2 text-right font-bold">{q.subtotal > 0 ? formatCurrency(q.subtotal) : 'TBD'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[q.status] || STATUS_COLORS.draft}`}>
                        {q.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap">
                      {q.order_ref ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold" title={`Converted ${q.order_ref.converted_at?.slice(0,10) || ''}`} data-testid={`quote-order-link-${i}`}>
                          <Package size={10} /> {q.order_ref.order_number}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-400 whitespace-nowrap">{q.status_updated_at ? formatDate(q.status_updated_at) : formatDate(q.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <select
                          value={q.status}
                          onChange={e => updateStatus(q.quote_number, e.target.value)}
                          className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white"
                          data-testid={`status-select-${i}`}
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                          <option value="expired">Expired</option>
                          <option value="closed">Closed</option>
                          <option value="revised">Revised</option>
                        </select>
                        <button
                          onClick={() => setExpandedQuote(expandedQuote === q.quote_number ? null : q.quote_number)}
                          className="p-1 hover:bg-blue-100 rounded transition-colors"
                          title="View details"
                          data-testid={`expand-quote-${i}`}
                        >
                          <Eye size={12} className="text-gray-400 hover:text-[#002FA7]" />
                        </button>
                        {q.status !== 'revised' && (
                          <button
                            onClick={() => startRevision(q)}
                            className="p-1 hover:bg-amber-100 rounded transition-colors"
                            title="Revise quote"
                            data-testid={`revise-quote-${i}`}
                          >
                            <Edit2 size={12} className="text-gray-400 hover:text-amber-600" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteQuote(q.quote_number)}
                          className="p-1 hover:bg-red-100 rounded transition-colors"
                          title="Delete quote"
                          data-testid={`delete-quote-${i}`}
                        >
                          <Trash2 size={12} className="text-gray-300 hover:text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Expanded Quote Detail */}
            {expandedQuote && (() => {
              const q = quotes.find(x => x.quote_number === expandedQuote);
              if (!q) return null;
              return (
                <div className="bg-blue-50/50 border-t border-blue-200 px-4 py-3" data-testid="quote-detail">
                  <div className="grid grid-cols-3 gap-4 text-xs mb-3">
                    <div>
                      <p className="font-bold text-gray-700">School</p>
                      <p className="text-gray-600">{q.contact?.school_name}</p>
                      <p className="text-gray-400">{q.contact?.school_address}</p>
                    </div>
                    <div>
                      <p className="font-bold text-gray-700">Purchasing Admin</p>
                      <p className="text-gray-600">{q.contact?.purchasing_admin_name || '-'}</p>
                      <p className="text-gray-400">{q.contact?.purchasing_admin_email || '-'}</p>
                    </div>
                    <div>
                      <p className="font-bold text-gray-700">IT Admin</p>
                      <p className="text-gray-600">{q.contact?.it_admin_name || '-'}</p>
                      <p className="text-gray-400">{q.contact?.it_admin_email || '-'}</p>
                    </div>
                  </div>
                  <table className="w-full text-[10px]">
                    <thead><tr className="text-gray-500"><th className="text-left py-1">SKU</th><th className="text-left py-1">Product</th><th className="text-center py-1">Qty</th><th className="text-right py-1">Unit</th><th className="text-right py-1">Total</th></tr></thead>
                    <tbody>
                      {(q.items || []).map((li, j) => (
                        <tr key={j} className="border-t border-blue-100">
                          <td className="py-1 font-mono text-[#002FA7] font-bold">{li.sku}</td>
                          <td className="py-1 text-gray-700">{li.name}</td>
                          <td className="py-1 text-center">{li.quantity}</td>
                          <td className="py-1 text-right">{formatCurrency(li.unit_price)}</td>
                          <td className="py-1 text-right font-bold">{formatCurrency(li.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {q.notes && <p className="mt-2 text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded">Notes: {q.notes}</p>}
                  {q.status === 'closed' && (
                    <FulfillmentTracker quote={q} onUpdate={handleFulfillmentUpdate} />
                  )}
                </div>
              );
            })()}
          </div>
        </>
      )}

      {activeView === 'pricing' && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-800">Product Pricing Editor</h3>
              <p className="text-xs text-gray-500">Edit prices below — changes take effect on new quotes immediately.</p>
            </div>
            <div className="flex gap-2">
              {hasEdits && (
                <Button onClick={savePricing} disabled={savingPrices} size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="save-pricing-btn">
                  {savingPrices ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                  Save {Object.keys(editingPrices).length} Change{Object.keys(editingPrices).length > 1 ? 's' : ''}
                </Button>
              )}
              <Button onClick={() => setShowAddProduct(!showAddProduct)} size="sm" variant={showAddProduct ? 'default' : 'outline'} className={showAddProduct ? 'bg-[#002FA7]' : ''} data-testid="add-product-toggle-btn">
                <Plus size={14} className="mr-1" /> Add Product
              </Button>
            </div>
          </div>

          {/* Add New Product Form */}
          {showAddProduct && (
            <div className="bg-white border-2 border-[#002FA7] rounded-lg p-5 space-y-4" data-testid="add-product-form">
              <h4 className="text-sm font-bold text-[#002FA7]">New Product</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">SKU *</label>
                  <Input value={newProduct.sku} onChange={e => setNewProduct(p => ({ ...p, sku: e.target.value.toUpperCase() }))} placeholder="MOS-XXX-YY" className="h-9 text-sm border-gray-300" data-testid="new-product-sku" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Product Name *</label>
                  <Input value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} placeholder="e.g., MOSAIC Workshop Materials" className="h-9 text-sm border-gray-300" data-testid="new-product-name" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Category</label>
                  <select value={newProduct.category} onChange={e => setNewProduct(p => ({ ...p, category: e.target.value }))} className="w-full h-9 text-sm border border-gray-300 rounded px-2" data-testid="new-product-category">
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Grade Band</label>
                  <Input value={newProduct.grade} onChange={e => setNewProduct(p => ({ ...p, grade: e.target.value }))} placeholder="e.g., K, 3-4, K-12" className="h-9 text-sm border-gray-300" data-testid="new-product-grade" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Price ($)</label>
                  <Input type="number" step="0.01" min="0" value={newProduct.price} onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))} placeholder="19.99" className="h-9 text-sm border-gray-300" data-testid="new-product-price" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Unit Label</label>
                  <Input value={newProduct.unit} onChange={e => setNewProduct(p => ({ ...p, unit: e.target.value }))} placeholder="per grade per class" className="h-9 text-sm border-gray-300" data-testid="new-product-unit" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Min Qty</label>
                  <Input type="number" min="1" value={newProduct.min_qty} onChange={e => setNewProduct(p => ({ ...p, min_qty: e.target.value }))} className="h-9 text-sm border-gray-300" data-testid="new-product-minqty" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={addProduct} disabled={addingProduct} size="sm" className="bg-[#002FA7] hover:bg-[#002FA7]/90" data-testid="add-product-submit-btn">
                  {addingProduct ? <RefreshCw size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />}
                  Add Product
                </Button>
                <Button onClick={() => setShowAddProduct(false)} size="sm" variant="outline" data-testid="add-product-cancel-btn">Cancel</Button>
              </div>
            </div>
          )}

          {Object.keys(CATEGORY_LABELS).map(cat => {
            const prods = grouped[cat] || [];
            if (prods.length === 0) return null;
            return (
              <div key={cat} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`pricing-category-${cat}`}>
                <div className="bg-gray-50 border-b px-4 py-2">
                  <h4 className="text-xs font-bold text-gray-700">{CATEGORY_LABELS[cat]}</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {prods.map(p => {
                    const isEditing = p.sku in editingPrices;
                    const displayPrice = isEditing ? editingPrices[p.sku] : p.price;
                    return (
                      <div key={p.sku} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50" data-testid={`pricing-${p.sku}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] font-bold text-[#002FA7] bg-blue-50 px-1.5 py-0.5 rounded">{p.sku}</span>
                            <span className="text-sm text-gray-800">{p.name}</span>
                            {p.is_custom && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">CUSTOM</span>}
                          </div>
                          <span className="text-[10px] text-gray-400">{p.unit} | Min qty: {p.min_qty}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={displayPrice}
                            onChange={e => setEditingPrices(prev => ({ ...prev, [p.sku]: e.target.value }))}
                            className={`w-24 text-right text-sm font-bold border rounded px-2 py-1 ${isEditing ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}
                            data-testid={`price-input-${p.sku}`}
                          />
                          {p.is_custom && (
                            <button onClick={() => deleteProduct(p.sku)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete custom product" data-testid={`delete-product-${p.sku}`}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Pricing Strategy Reference */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
            <p className="font-bold mb-1">Pricing Strategy Reference</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><strong>Print Packs:</strong> $299/grade (20 copies + Teacher Edition)</li>
              <li><strong>Print Bundles:</strong> Flat additive pricing per grade (no discounts per NYC procurement rules)</li>
              <li><strong>Digital Licenses:</strong> $999/grade/year per building site license. Contact for district/multi-building pricing.</li>
              <li><strong>Complete Bundles:</strong> $1,298/grade per building (Print + Digital)</li>
              <li><strong>Teacher Guide & Tool Kit:</strong> $19.99/grade/class</li>
              <li><strong>Custom products:</strong> Use "Add Product" above to create new items with any pricing</li>
            </ul>
          </div>
        </>
      )}
    </div>

      {/* Revision Modal */}
      {revisingQuote && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setRevisingQuote(null)} data-testid="revision-modal">
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b-2 border-gray-200">
              <div>
                <h2 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Revise Quote</h2>
                <p className="text-xs text-gray-400 mt-0.5">Original: {revisingQuote.quote_number} — {revisingQuote.contact?.school_name}</p>
              </div>
              <button onClick={() => setRevisingQuote(null)} className="p-1 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Contact (read-only) */}
              <div className="bg-gray-50 border border-gray-200 p-3 text-xs">
                <strong>School:</strong> {revisingQuote.contact?.school_name} | <strong>Contact:</strong> {revisingQuote.contact?.contact_name} ({revisingQuote.contact?.contact_email})
              </div>

              {/* Line Items */}
              <div>
                <p className="text-sm font-bold mb-2">Line Items</p>
                {revisionItems.map((item, idx) => {
                  const product = products.find(p => p.sku === item.sku);
                  return (
                    <div key={idx} className="flex items-center gap-3 mb-2 border-b border-gray-100 pb-2">
                      <div className="flex-1 text-sm">{product?.name || item.sku}</div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-400">Qty:</label>
                        <input
                          type="number" min="1"
                          value={item.quantity}
                          onChange={e => {
                            const updated = [...revisionItems];
                            updated[idx].quantity = parseInt(e.target.value) || 1;
                            setRevisionItems(updated);
                          }}
                          className="w-16 h-8 border-2 border-gray-300 px-2 text-sm text-center"
                          data-testid={`revision-qty-${idx}`}
                        />
                      </div>
                      <button onClick={() => setRevisionItems(revisionItems.filter((_, i) => i !== idx))} className="p-1 hover:bg-red-50">
                        <Trash2 size={14} className="text-gray-300 hover:text-red-500" />
                      </button>
                    </div>
                  );
                })}

                {/* Add product */}
                <select
                  onChange={e => {
                    if (e.target.value) {
                      setRevisionItems([...revisionItems, { sku: e.target.value, quantity: 1 }]);
                      e.target.value = '';
                    }
                  }}
                  className="w-full h-8 border-2 border-dashed border-gray-300 text-xs text-gray-400 mt-2"
                  data-testid="revision-add-product"
                >
                  <option value="">+ Add a product...</option>
                  {products.map(p => (
                    <option key={p.sku} value={p.sku}>{p.name} — {formatCurrency(p.price)}/{p.unit}</option>
                  ))}
                </select>
              </div>

              {/* Effective date — backdate / forward-date the revised quote PDF */}
              <div>
                <label className="text-sm font-bold block mb-1">Quote Date (effective)</label>
                <input
                  type="date"
                  value={revisionEffectiveDate}
                  onChange={e => setRevisionEffectiveDate(e.target.value)}
                  className="h-9 border-2 border-gray-300 px-2 text-sm"
                  data-testid="revision-effective-date"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Defaults to today. Original `created_at` audit timestamp is preserved separately.</p>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-bold block mb-1">Notes</label>
                <textarea
                  value={revisionNotes}
                  onChange={e => setRevisionNotes(e.target.value)}
                  className="w-full h-20 border-2 border-gray-300 p-2 text-sm"
                  placeholder="Reason for revision, special instructions..."
                  data-testid="revision-notes"
                />
              </div>

              {/* Send email */}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={revisionSendEmail} onChange={e => setRevisionSendEmail(e.target.checked)} data-testid="revision-send-email" />
                Email revised quote to {revisingQuote.contact?.purchasing_admin_email || revisingQuote.contact?.contact_email || 'contact'}
              </label>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-gray-200">
                <Button
                  onClick={submitRevision}
                  disabled={submittingRevision || revisionItems.length === 0}
                  className="bg-[#002FA7] text-white text-xs h-9 flex-1"
                  style={{ borderRadius: 0 }}
                  data-testid="submit-revision-btn"
                >
                  {submittingRevision ? 'Creating...' : `Create Revision (${revisingQuote.quote_number}-REV)`}
                </Button>
                <Button variant="outline" onClick={() => setRevisingQuote(null)} className="text-xs h-9" style={{ borderRadius: 0 }}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Delete Quotes Modal (password-gated) ── */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-delete-dialog">
          <div className="bg-white border-2 border-red-300 shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              <h3 className="text-lg font-bold">Delete {selected.size} Quote{selected.size === 1 ? '' : 's'}</h3>
            </div>

            <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-800 leading-relaxed">
              <div className="font-semibold mb-1">This cannot be undone.</div>
              The {selected.size} selected quote{selected.size === 1 ? '' : 's'} will be permanently removed.
              Cached PDFs on disk will be cleaned up. Quotes that have already been converted to an Order
              will be <b>skipped</b> (delete the Order first to keep analytics consistent).
              An audit row is written for each deletion.
            </div>

            <div className="max-h-28 overflow-y-auto bg-gray-50 border border-gray-200 p-2 text-[11px] font-mono text-gray-700">
              {Array.from(selected).slice(0, 20).map(q => <div key={q}>{q}</div>)}
              {selected.size > 20 && <div className="text-gray-400">…and {selected.size - 20} more</div>}
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Your Super Admin password *</label>
              <Input
                type="password"
                value={bulkDeletePassword}
                onChange={(e) => setBulkDeletePassword(e.target.value)}
                placeholder="Re-enter password to confirm"
                autoFocus
                data-testid="bulk-delete-password"
              />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Reason (optional, logged for audit)</label>
              <Input
                value={bulkDeleteReason}
                onChange={(e) => setBulkDeleteReason(e.target.value)}
                placeholder="e.g. TEST / DEMO / SPAM cleanup"
                data-testid="bulk-delete-reason"
              />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">
                Type <span className="font-mono font-bold text-red-700">DELETE</span> to confirm *
              </label>
              <Input
                value={bulkDeleteConfirmText}
                onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="bulk-delete-confirm-text"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBulkDelete(false)} disabled={bulkDeleting}>Cancel</Button>
              <Button
                onClick={submitBulkDelete}
                disabled={
                  bulkDeleting ||
                  !bulkDeletePassword ||
                  bulkDeleteConfirmText.trim().toUpperCase() !== 'DELETE'
                }
                className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
                data-testid="bulk-delete-submit"
              >
                <Trash2 size={14} /> {bulkDeleting ? 'Deleting…' : `Delete ${selected.size}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuotesManagementTab;
